import React from 'react';
import { Checkbox } from '@mui/material';
import {
    Images,
    masteryDirectionEnums,
    masteryDirectionList,
    PermissionList,
    renderStaffName,
    RolePermission,
    tagEnums,
    toCamelCase,
} from 'utils';
import { DateRow, EditWithIcon, FilterTags, HtmlConvertor, LinkRow, ManageActions, SimpleTooltip, TagComponent, TextRow } from 'components';
import { TaskStatusBox } from './fragments';
import { canReadTags } from 'store/tags/tag.utils';

export const taskTypeEnums = {
    TABLE: 'TABLE',
    CALENDAR: 'CALENDAR',
    BOARD: 'BOARD',
};

export const taskTypesList = [
    { title: 'Table', tabValue: taskTypeEnums.TABLE },
    // { title: 'Calendar', tabValue: taskTypeEnums.CALENDAR },
    { title: 'Board', tabValue: taskTypeEnums.BOARD },
];

export const taskOnModelEnums = {
    CLIENT: 'client',
    STAFF: 'staff',
    FUNDER: 'funder',
};

export const taskOnModelList = [
    { id: taskOnModelEnums.CLIENT, name: 'Client' },
    { id: taskOnModelEnums.STAFF, name: 'Staff' },
    { id: taskOnModelEnums.FUNDER, name: 'Payer' },
];

export const taskOnModelColors = {
    [taskOnModelEnums.CLIENT]: { color: '#5925DC', background: '#F4F3FF', border: '#F4F3FF' },
    [taskOnModelEnums.STAFF]: { color: '#175CD3', background: '#EFF8FF', border: '#EFF8FF' },
    [taskOnModelEnums.FUNDER]: { color: '#B54708', background: '#FFFAEB', border: '#FFFAEB' },
};

const onModelLabels = {
    [taskOnModelEnums.CLIENT]: 'Client',
    [taskOnModelEnums.STAFF]: 'Staff',
    [taskOnModelEnums.FUNDER]: 'Payer',
};

export const renderResourceBadge = (item) => {
    if (!item?.onModel) return '--';
    const colors = taskOnModelColors[item.onModel] || {};
    const label = onModelLabels[item.onModel] || item.onModel;
    const resourceName = item?.resource?.name || '';
    const resourceId = item?.resource?.id || '';

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
                style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.color,
                    background: colors.background,
                    border: `1px solid ${colors.border}`,
                    whiteSpace: 'nowrap',
                }}>
                {label}
            </span>

            <span>
                {item.onModel === taskOnModelEnums.STAFF && resourceName && (
                    <LinkRow
                        name={resourceName}
                        href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${resourceId}` : ''}
                        checkPermission={resourceId ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                        textWidth={8}
                    />
                )}
                {item.onModel === taskOnModelEnums.CLIENT && resourceName && (
                    <LinkRow
                        name={resourceName}
                        href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${resourceId}` : ''}
                        checkPermission={resourceId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        textWidth={8}
                    />
                )}
                {item.onModel === taskOnModelEnums.FUNDER && resourceName && (
                    <LinkRow
                        name={resourceName}
                        href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${resourceId}` : ''}
                        checkPermission={resourceId ? RolePermission([PermissionList.FS_READ.code]) : null}
                    />
                )}
            </span>
        </span>
    );
};

export const taskStatusEnums = {
    PENDING: 'PENDING',
    ASSIGNED: 'ASSIGNED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};

export const taskStatusList = [
    { name: 'Pending', id: taskStatusEnums.PENDING },
    { name: 'Assigned', id: taskStatusEnums.ASSIGNED },
    { name: 'In Progress', id: taskStatusEnums.IN_PROGRESS },
    { name: 'Completed', id: taskStatusEnums.COMPLETED },
    { name: 'Cancelled', id: taskStatusEnums.CANCELLED },
];

const checkManageTask = RolePermission([PermissionList.TASK_MANAGE?.code, PermissionList.TASK_FULL_ACCESS?.code]);

export const taskHead = (handleSelect, fullList, selected) => {
    return [
        {
            renderView: (
                <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                    <Checkbox
                        onClick={(e) => handleSelect(e, '', 'all')}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#347AF0' }}
                        checked={fullList?.length === selected?.length}
                    />
                </SimpleTooltip>
            ),
            width: '50px',
        },
        { name: 'title', title: 'Task Name' },
        { name: 'description', title: 'Description', custom: false },
        { name: 'assignee', title: 'Assignee', custom: false },
        { name: 'reporter', title: 'Reporter', custom: false },
        { name: 'dueDate', title: 'Due Date', iconType: 'date' },
        {
            name: 'onModel',
            title: 'Resource',
            filterList: [{ name: 'All', id: 'All' }, ...taskOnModelList],
            disabled: true,
        },
        {
            name: 'statuses',
            title: 'Status',
            multiple: true,
            returnAllValues: true,
            filterList: [{ name: 'All', id: 'All' }, ...taskStatusList],
        },
        canReadTags() && { name: '', title: 'Tags', custom: false, renderView: <FilterTags /> },
        checkManageTask && { name: '', title: 'Actions', custom: false, disabled: true, width: '60px' },
    ];
};

export const taskBody = (handleSelect, fullList, selected, requestParams) => {
    return [
        {
            button: (handle, item) => (
                <Checkbox
                    onClick={(e) => handleSelect(e, item)}
                    name={'checkBox'}
                    style={{
                        padding: 0,
                        color: '#347AF0',
                    }}
                    checked={selected?.includes(item?.id)}
                />
            ),
            notClickable: true,
        },
        { rowText: (item) => <TextRow name={item?.title} /> },
        // { rowText: (item) => <TextRow name={item?.description} /> },
        { rowText: (item) => <HtmlConvertor text={item?.description} /> },
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.assignee?.id ? renderStaffName(item.assignee) : '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.assignee?.id}` : ''}
                    checkPermission={item?.assignee?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                />
            ),
        },
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.reporter?.id ? renderStaffName(item.reporter) : '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.reporter?.id}` : ''}
                    checkPermission={item?.reporter?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                />
            ),
        },
        { rowText: (item) => <DateRow date={item?.dueDate} /> },

        { rowText: (item) => renderResourceBadge(item), notClickable: false },

        { rowText: (item) => <TaskStatusBox info={item} requestParams={requestParams}/> },

        canReadTags() && {
            rowText: (item) => <TagComponent userInfo={item} module={tagEnums.TASK} requestParams={requestParams} />,
            notClickable: true,
        },
        checkManageTask && {
            button: (fn, item) => (
                <ManageActions
                    btnClass="edit-btn-box"
                    btnIcon={Images.moreGrayIcon}
                    styleBodyItems={{ padding: 0 }}
                    renderBody={(handleClose) => (
                        <div className="popover-action-btn">
                            <button
                                onClick={() => {
                                    handleClose();
                                    fn('edit');
                                }}>
                                <img src={Images.edit} alt="icon" />
                                <p>Edit</p>
                            </button>
                            <button
                                onClick={() => {
                                    handleClose();
                                    fn('delete');
                                }}>
                                <img src={Images.deleteTag} alt="icon" />
                                <p>Delete</p>
                            </button>
                        </div>
                    )}
                />
            ),
            notClickable: true,
        },
    ];
};

export const ACTION_TYPE = 'GET_TASKS';

export const taskNotYet = {
    title: 'No Tasks Yet',
    subTitle: 'Create a task to start tracking work for your team.',
    image: Images.noClientYet,
};
